import { t } from '../i18n';
import type { TranslationKey } from '../i18n/types';
import { state } from '../state';
import { navigateTo } from '../router';

// ============================================================
// Карточка «Челлендж недели» — общая для дайджеста и личной статистики.
// ============================================================

export interface ChallengeResponse {
  week: { start: string; end: string; days_left: number; index: number };
  challenge: {
    id: 'wins' | 'games' | 'map_win' | 'race_win' | 'top2' | 'unique_maps' | 'streak';
    target: number;
    params: Record<string, string | number>;
  };
  player: { id: number; name: string } | null;
  progress: number | null;
  done: boolean;
}

const TEXT_KEYS: Record<ChallengeResponse['challenge']['id'], TranslationKey> = {
  wins: 'challenge.wins',
  games: 'challenge.games',
  map_win: 'challenge.map_win',
  race_win: 'challenge.race_win',
  top2: 'challenge.top2',
  unique_maps: 'challenge.unique_maps',
  streak: 'challenge.streak',
};

export function buildChallengeCard(
  data: ChallengeResponse,
  options: { compact?: boolean } = {},
): HTMLElement {
  const card = document.createElement('div');
  card.className = 'challenge-card' + (data.done ? ' is-done' : '');

  const head = document.createElement('div');
  head.className = 'challenge-head';

  const title = document.createElement('h3');
  title.textContent = t('challenge.title');

  const days = document.createElement('span');
  days.className = 'challenge-days';
  days.textContent = t('challenge.days_left', { n: data.week.days_left });

  head.append(title, days);

  const text = document.createElement('p');
  text.className = 'challenge-text';
  // Раса в задании приходит кодом (T/Z/P) — подписываем полным названием.
  const raceNames: Record<string, string> = { T: 'Terran', Z: 'Zerg', P: 'Protoss' };
  const params: Record<string, string | number> = { ...data.challenge.params };
  if (typeof params.race === 'string') params.race = raceNames[params.race] ?? params.race;
  params.n = params.n ?? data.challenge.target;
  text.textContent = t(TEXT_KEYS[data.challenge.id] ?? 'challenge.wins', params);

  card.append(head, text);

  if (data.progress != null) {
    const progress = Math.max(0, Math.min(data.progress, data.challenge.target));
    const bar = document.createElement('div');
    bar.className = 'challenge-progress';

    const fill = document.createElement('div');
    fill.className = 'challenge-progress-bar';
    fill.style.width = `${Math.round((progress / Math.max(1, data.challenge.target)) * 100)}%`;
    bar.appendChild(fill);

    const foot = document.createElement('div');
    foot.className = 'challenge-foot';

    const counter = document.createElement('span');
    counter.textContent = t('challenge.progress', {
      current: data.progress,
      target: data.challenge.target,
    });

    foot.appendChild(counter);
    if (data.done) {
      const done = document.createElement('span');
      done.className = 'challenge-done';
      done.textContent = t('challenge.done');
      foot.appendChild(done);
    }

    if (!options.compact) {
      const open = document.createElement('button');
      open.type = 'button';
      open.className = 'btn-secondary';
      open.textContent = t('challenge.open');
      open.addEventListener('click', () => navigateTo('/my-stats'));
      foot.appendChild(open);
    }

    card.append(bar, foot);
  } else {
    const foot = document.createElement('div');
    foot.className = 'challenge-foot';

    if (!state.token) {
      const link = document.createElement('a');
      link.href = '#';
      link.className = 'hint-link';
      link.textContent = t('challenge.login');
      link.addEventListener('click', (e) => {
        e.preventDefault();
        sessionStorage.setItem(
          'redirectAfterLogin',
          window.location.pathname + window.location.search,
        );
        navigateTo('/auth');
      });
      foot.appendChild(link);
    } else {
      const hint = document.createElement('span');
      hint.textContent = t('challenge.unlinked');
      foot.appendChild(hint);
    }

    card.appendChild(foot);
  }

  return card;
}
